import React, { useState, useRef } from "react";
import { media } from "../services/api";
import { PlusIcon, CloseIcon, TrashIcon, ImageIcon, CheckIcon } from "./Icons";

function MediaManager({ entityId, mediaItems, onMediaChange }) {
  const [loading, setLoading] = useState(false);
  const [showAddUrl, setShowAddUrl] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      // Upload files sequentially, first one is primary if no existing media
      for (let i = 0; i < files.length; i++) {
        const isPrimary = mediaItems.length === 0 && i === 0;
        await media.upload(entityId, files[i], isPrimary);
      }
      onMediaChange();
    } catch (err) {
      setError("Failed to upload image(s)");
      console.error(err);
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleAddUrl = async () => {
    if (!newUrl.trim()) return;

    setLoading(true);
    setError(null);
    try {
      const isPrimary = mediaItems.length === 0;
      await media.addFromUrl(entityId, newUrl.trim(), isPrimary);
      setNewUrl("");
      setShowAddUrl(false);
      onMediaChange();
    } catch (err) {
      setError("Failed to add image from URL");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (mediaId) => {
    setLoading(true);
    setError(null);
    try {
      await media.setPrimary(mediaId);
      onMediaChange();
    } catch (err) {
      setError("Failed to set primary image");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    // Deletion is permanent — guard against a mistap on the small button.
    if (!window.confirm("Delete this photo?")) return;

    setLoading(true);
    setError(null);
    try {
      await media.delete(mediaId);
      onMediaChange();
    } catch (err) {
      setError("Failed to delete image");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="media-manager">
      <label>Images</label>

      {error && (
        <div
          style={{
            color: "var(--danger)",
            fontSize: "0.875rem",
            marginBottom: "var(--spacing-sm)",
          }}
        >
          {error}
        </div>
      )}

      {/* Image Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
          gap: "var(--spacing-sm)",
          marginBottom: "var(--spacing-md)",
        }}
      >
        {mediaItems.map((item) => (
          <div
            key={item.id}
            style={{
              position: "relative",
              aspectRatio: "1",
              borderRadius: "var(--radius-md)",
              overflow: "hidden",
              border: item.is_primary
                ? "2px solid var(--accent-primary)"
                : "1px solid var(--border-color)",
              background: "var(--bg-secondary)",
            }}
          >
            <img
              src={media.getImageUrl(item.file_path)}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            {/* Primary badge */}
            {item.is_primary && (
              <div
                style={{
                  position: "absolute",
                  top: "4px",
                  left: "4px",
                  background: "var(--accent-primary)",
                  color: "white",
                  fontSize: "0.65rem",
                  padding: "2px 6px",
                  borderRadius: "var(--radius-sm)",
                  fontWeight: 600,
                }}
              >
                Primary
              </div>
            )}

            {/* Action buttons */}
            <div
              style={{
                position: "absolute",
                bottom: "4px",
                right: "4px",
                display: "flex",
                gap: "4px",
              }}
            >
              {!item.is_primary && (
                <button
                  onClick={() => handleSetPrimary(item.id)}
                  disabled={loading}
                  title="Set as primary"
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "var(--radius-sm)",
                    background: "rgba(0,0,0,0.6)",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckIcon size={14} />
                </button>
              )}
              <button
                onClick={() => handleDelete(item.id)}
                disabled={loading}
                title="Delete image"
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "var(--radius-sm)",
                  background: "rgba(0,0,0,0.6)",
                  border: "none",
                  color: "var(--danger)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TrashIcon size={14} />
              </button>
            </div>
          </div>
        ))}

        {/* Empty state */}
        {mediaItems.length === 0 && !showAddUrl && (
          <div
            style={{
              gridColumn: "1 / -1",
              padding: "var(--spacing-lg)",
              textAlign: "center",
              color: "var(--text-muted)",
              background: "var(--bg-secondary)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <ImageIcon size={32} />
            <p style={{ margin: "var(--spacing-sm) 0 0" }}>No images yet</p>
          </div>
        )}
      </div>

      {/* Add URL Form */}
      {showAddUrl && (
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-sm)",
            marginBottom: "var(--spacing-md)",
          }}
        >
          <input
            type="url"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            style={{ flex: 1 }}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddUrl();
              } else if (e.key === "Escape") {
                setShowAddUrl(false);
                setNewUrl("");
              }
            }}
          />
          <button
            onClick={handleAddUrl}
            disabled={loading || !newUrl.trim()}
            className="btn btn-primary"
          >
            Add
          </button>
          <button
            onClick={() => {
              setShowAddUrl(false);
              setNewUrl("");
            }}
            className="btn btn-ghost btn-icon"
          >
            <CloseIcon size={16} />
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept="image/jpeg,image/png,image/webp"
          multiple
          style={{ display: "none" }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
          className="btn btn-secondary"
          style={{ flex: 1 }}
        >
          <PlusIcon size={16} />
          Upload Image
        </button>
        <button
          onClick={() => setShowAddUrl(true)}
          disabled={loading || showAddUrl}
          className="btn btn-secondary"
          style={{ flex: 1 }}
        >
          <PlusIcon size={16} />
          Add from URL
        </button>
      </div>

      {loading && (
        <div
          style={{
            textAlign: "center",
            marginTop: "var(--spacing-sm)",
            color: "var(--text-muted)",
            fontSize: "0.875rem",
          }}
        >
          Processing...
        </div>
      )}
    </div>
  );
}

export default MediaManager;
